/**
 * 本将棋の TypeScript 表現 (mixed-engine の serde 形と一致)。
 *
 * 座標系: `[row, col]` で row=0 が Sente (First) 最下段、 row=8 が Gote 最下段。
 * col=0 が Sente から見た最左 (= USI 9筋)、 col=8 が右 (= USI 1筋)。
 */

export type Side = 'First' | 'Second';

export type PieceKind =
  | 'Pawn'
  | 'Lance'
  | 'Knight'
  | 'Silver'
  | 'Gold'
  | 'Bishop'
  | 'Rook'
  | 'King'
  | 'Tokin'
  | 'Narikyo'
  | 'Narikei'
  | 'Narigin'
  | 'Horse'
  | 'Dragon';

/** 打てる持ち駒 (生駒のみ) */
export type DropKind = 'Pawn' | 'Lance' | 'Knight' | 'Silver' | 'Gold' | 'Bishop' | 'Rook';

export interface Piece {
  kind: PieceKind;
  owner: Side;
}

export interface HandPile {
  pawn: number;
  lance: number;
  knight: number;
  silver: number;
  gold: number;
  bishop: number;
  rook: number;
}

export type ShogiWinReason = 'Checkmate' | 'Try' | 'Resign' | 'Timeout' | 'IllegalRepeat';
export type ShogiDrawReason = 'Sennichite' | 'DrawAgreement';

export type ShogiOutcome =
  | { Win: { winner: Side; reason: ShogiWinReason } }
  | { Draw: { reason: ShogiDrawReason } };

export interface ShogiState {
  board: (Piece | null)[][];
  side: Side;
  hands: [HandPile, HandPile];
  ply: number;
  position_history: string[];
  outcome: ShogiOutcome | null;
}

export type ShogiMove =
  | { Push: { from: [number, number]; to: [number, number]; promote: boolean } }
  | { Drop: { kind: DropKind; to: [number, number] } };

export const SHOGI_ROWS = 9;
export const SHOGI_COLS = 9;

/** 持ち駒スロットの順 (UI 表示用)。 */
export const HAND_ORDER: DropKind[] = ['Rook', 'Bishop', 'Gold', 'Silver', 'Knight', 'Lance', 'Pawn'];

export function isPushMove(m: ShogiMove): m is { Push: { from: [number, number]; to: [number, number]; promote: boolean } } {
  return typeof m === 'object' && 'Push' in m;
}

export function isDropMove(m: ShogiMove): m is { Drop: { kind: DropKind; to: [number, number] } } {
  return typeof m === 'object' && 'Drop' in m;
}

/** 盤上駒の from 一致で legal から候補を抽出。 */
export function legalTargetsFor(legal: ShogiMove[], from: [number, number]): ShogiMove[] {
  return legal.filter(
    (m) => isPushMove(m) && m.Push.from[0] === from[0] && m.Push.from[1] === from[1],
  );
}

/** 駒打ち (kind) の打てるマス候補。 */
export function legalDropsFor(legal: ShogiMove[], kind: DropKind): ShogiMove[] {
  return legal.filter((m) => isDropMove(m) && m.Drop.kind === kind);
}

export function moveTo(m: ShogiMove): [number, number] {
  if (isPushMove(m)) return m.Push.to;
  return m.Drop.to;
}

/** 持ち駒に駒があるか。 */
export function handCount(hand: HandPile, kind: DropKind): number {
  switch (kind) {
    case 'Pawn':
      return hand.pawn;
    case 'Lance':
      return hand.lance;
    case 'Knight':
      return hand.knight;
    case 'Silver':
      return hand.silver;
    case 'Gold':
      return hand.gold;
    case 'Bishop':
      return hand.bishop;
    case 'Rook':
      return hand.rook;
  }
}

/** 持ち駒側 (0 = First, 1 = Second)。 */
export function handIndex(side: Side): 0 | 1 {
  return side === 'First' ? 0 : 1;
}

/** 駒の漢字グリフ。 */
export const PIECE_KANJI: Record<PieceKind, string> = {
  Pawn: '歩',
  Lance: '香',
  Knight: '桂',
  Silver: '銀',
  Gold: '金',
  Bishop: '角',
  Rook: '飛',
  King: '玉',
  Tokin: 'と',
  Narikyo: '杏',
  Narikei: '圭',
  Narigin: '全',
  Horse: '馬',
  Dragon: '龍',
};

/** 成り元の判定 (UI で「不成も選べる」を出すため)。 */
export function canPromote(piece: Piece, from: [number, number], to: [number, number]): boolean {
  const promotable: PieceKind[] = ['Pawn', 'Lance', 'Knight', 'Silver', 'Bishop', 'Rook'];
  if (!promotable.includes(piece.kind)) return false;
  const inZone = piece.owner === 'First' ? from[0] >= 6 || to[0] >= 6 : from[0] <= 2 || to[0] <= 2;
  return inZone;
}
